import React, { useState, useMemo } from 'react';
import { ProductReview } from '../../domain/types';
import { ReviewSortOption, REVIEW_SORT_OPTIONS, ReportReason, REPORT_REASONS } from './types';
import { useAuth } from '../../state/auth';
import { useToast } from '../../state/ToastContext';
import { formatRelativeTimeFull } from '../../utils/formatters';
import { ReviewCard } from '../../components/reviews/ReviewCard';

interface ReviewsListProps {
  reviews: ProductReview[];
  onMarkHelpful?: (reviewId: string) => Promise<void>;
  onReport?: (reviewId: string, reason: ReportReason, description?: string) => Promise<void>;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoading?: boolean;
  showSort?: boolean;
}

export const ReviewsList: React.FC<ReviewsListProps> = ({
  reviews,
  onMarkHelpful,
  onReport,
  onLoadMore,
  hasMore = false,
  isLoading = false,
  showSort = true,
}) => {
  const [sortBy, setSortBy] = useState<ReviewSortOption['value']>('newest');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const sortedReviews = useMemo(() => {
    const sorted = [...reviews];
    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      case 'highest':
        return sorted.sort((a, b) => b.rating - a.rating);
      case 'lowest':
        return sorted.sort((a, b) => a.rating - b.rating);
      case 'helpful':
        return sorted.sort((a, b) => b.helpful - a.helpful);
      default:
        return sorted;
    }
  }, [reviews, sortBy]);

  if (reviews.length === 0) {
    return (
      <div className="text-center py-8">
        <span className="material-symbols-outlined text-4xl text-gray-300">rate_review</span>
        <p className="mt-4 text-gray-500">Aún no hay reseñas</p>
        <p className="text-sm text-gray-400 mt-2">¡Sé el primero en opinar!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Sort Controls */}
      {showSort && reviews.length > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-text-secondary">
            {reviews.length} {reviews.length === 1 ? 'reseña' : 'reseñas'}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Ordenar por:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as ReviewSortOption['value'])}
              className="text-sm border border-gray-200 dark:border-white/10 rounded-lg px-3 py-1.5 bg-white dark:bg-surface-dark focus:outline-none focus:ring-2 focus:ring-primary/20"
            >
              {REVIEW_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Reviews */}
      <div className="space-y-4">
        {sortedReviews.map((review) => (
          <ReviewCard
            key={review.id}
            review={review}
            onHelpful={onMarkHelpful}
            showShopLink={false}
          />
        ))}
      </div>

      {/* Load More */}
      {hasMore && onLoadMore && (
        <div className="text-center pt-4">
          <button
            onClick={onLoadMore}
            disabled={isLoading}
            className="px-6 py-2 border border-gray-200 dark:border-white/10 rounded-lg font-medium text-sm hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Cargando...' : 'Cargar más reseñas'}
          </button>
        </div>
      )}
    </div>
  );
};
